import { useMemo } from 'react';
import DataTable, { Column } from '@shared/ui/data-table';
import { useTicketStatusDict } from '../../../shared/lib/ticket-statuses';
import hexConverter from '@entities/issue/lib/hex-converter';
import { formatDate } from '@entities/diagnostics/lib';
import type { DiagnosticTicket } from '../../../types/diagnosticsInterfaces';

interface Props {
  rows: DiagnosticTicket[];
  loading: boolean;
  isStaff: boolean;
  onRowClick: (t: DiagnosticTicket) => void;
}

export function DiagnosticTicketsTable({ rows, loading, isStaff, onRowClick }: Props) {
  const statusDict = useTicketStatusDict();

  const columns: Column<DiagnosticTicket>[] = useMemo(() => {
    const base: Column<DiagnosticTicket>[] = [
      {
        key: 'number',
        header: '№',
        headerClassName: 'w-[1%] whitespace-nowrap',
        render: (t) => (
          <span className="text-[#2d2c2c] font-mono text-xs">{t.number}</span>
        ),
      },
      {
        key: 'subject',
        header: 'Тема',
        render: (t) => (
          <div className="min-w-0">
            <p className="font-medium text-[#2d2c2c] truncate max-w-[380px]" title={t.subject}>
              {t.subject || 'Без темы'}
            </p>
            <p className="text-xs text-gray-400 truncate">
              {t.devices.length} устр.
              {t.devices[0] ? ` · ${t.devices[0].model_display}` : ''}
              {t.devices.length > 1 ? ' …' : ''}
            </p>
          </div>
        ),
      },
    ];
    if (isStaff) {
      base.push({
        key: 'company',
        header: 'Компания',
        render: (t) =>
          t.company?.name ? (
            <span className="text-[#2d2c2c] text-xs truncate max-w-[220px] inline-block" title={t.company.name}>
              {t.company.name}
            </span>
          ) : t.sender_email ? (
            <span className="text-gray-500 text-xs" title={t.sender_email}>{t.sender_email}</span>
          ) : (
            <span className="text-gray-400 text-xs">—</span>
          ),
      });
    }
    base.push(
      {
        key: 'type',
        header: 'Тип',
        render: (t) => (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
            {t.ticket_type?.name ?? '—'}
          </span>
        ),
      },
      {
        key: 'status',
        header: 'Статус',
        render: (t) => {
          const color = statusDict.colorFor(t.status);
          return (
            <span
              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
              style={{ color, backgroundColor: hexConverter(color) }}
            >
              {statusDict.labelFor(t.status)}
            </span>
          );
        },
      },
      {
        key: 'created_at',
        header: 'Создан',
        render: (t) => <span className="text-gray-600 text-xs">{formatDate(t.created_at)}</span>,
      },
    );
    if (isStaff) {
      base.push({
        key: 'assignee',
        header: 'Ответственный',
        render: (t) => (
          <span className="text-gray-600 text-xs">{t.assigned_to?.username ?? '—'}</span>
        ),
      });
    }
    return base;
  }, [isStaff, statusDict]);

  return (
    <DataTable<DiagnosticTicket>
      columns={columns}
      data={rows}
      keyAccessor={(t) => t.id}
      onRowClick={onRowClick}
      loading={loading && rows.length === 0}
      emptyMessage="Обращений на диагностику пока нет"
    />
  );
}
